interface UseWorkflowRunPaginationParams {
  page: number;
  pageSize: number;
  totalRuns: number;
  currentPageRunsCount: number;
  setPage: (page: number) => void;
  /**
   * False while the current query has no result yet (loading a new offset
   * without placeholder data). Clamping against a fallback total of 0 would
   * kick the user back to page 1 until that page had been cached.
   */
  totalIsKnown: boolean;
}

interface UseWorkflowRunPaginationResult {
  totalPages: number;
  effectivePage: number;
  pageStartIndex: number;
  pageEndIndex: number;
  isOnFirstPage: boolean;
  isOnLastPage: boolean;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
  /**
   * When set, the caller that owns `page` should write this during render
   * (`if (pageCorrection !== null) setPage(pageCorrection)`). That is the
   * same-component state adjustment React allows; this hook must not invoke
   * `setPage` itself during render or in an effect.
   */
  pageCorrection: number | null;
}

export interface ResolvedWorkflowRunPagination {
  totalPages: number;
  effectivePage: number;
  pageStartIndex: number;
  pageEndIndex: number;
  isOnFirstPage: boolean;
  isOnLastPage: boolean;
  pageCorrection: number | null;
}

export function resolveWorkflowRunPagination({
  page,
  pageSize,
  totalRuns,
  currentPageRunsCount,
  totalIsKnown,
}: {
  page: number;
  pageSize: number;
  totalRuns: number;
  currentPageRunsCount: number;
  totalIsKnown: boolean;
}): ResolvedWorkflowRunPagination {
  const totalPages = Math.max(1, Math.ceil(totalRuns / pageSize));
  const shouldClamp = totalIsKnown && page > totalPages;
  const effectivePage = Math.max(
    1,
    shouldClamp ? totalPages : totalIsKnown ? Math.min(page, totalPages) : page,
  );
  const pageOffset = (effectivePage - 1) * pageSize;
  const pageStartIndex = currentPageRunsCount > 0 ? pageOffset + 1 : 0;
  const pageEndIndex =
    currentPageRunsCount > 0
      ? Math.min(pageOffset + currentPageRunsCount, totalRuns)
      : 0;

  return {
    totalPages,
    effectivePage,
    pageStartIndex,
    pageEndIndex,
    isOnFirstPage: effectivePage <= 1,
    // Until the total is known, don't treat this as the last page — and don't
    // advertise a next page either. The Next button stays disabled on first
    // load; after a click it stays on the requested page while that offset
    // fetches instead of snapping back to page 1.
    isOnLastPage: !totalIsKnown || effectivePage >= totalPages,
    pageCorrection: shouldClamp ? totalPages : null,
  };
}

export function useWorkflowRunPagination({
  page,
  pageSize,
  totalRuns,
  currentPageRunsCount,
  setPage,
  totalIsKnown,
}: UseWorkflowRunPaginationParams): UseWorkflowRunPaginationResult {
  const resolved = resolveWorkflowRunPagination({
    page,
    pageSize,
    totalRuns,
    currentPageRunsCount,
    totalIsKnown,
  });

  return {
    totalPages: resolved.totalPages,
    effectivePage: resolved.effectivePage,
    pageStartIndex: resolved.pageStartIndex,
    pageEndIndex: resolved.pageEndIndex,
    isOnFirstPage: resolved.isOnFirstPage,
    isOnLastPage: resolved.isOnLastPage,
    pageCorrection: resolved.pageCorrection,
    goToPreviousPage: () => {
      setPage(Math.max(1, page - 1));
    },
    goToNextPage: () => {
      if (totalIsKnown && page >= resolved.totalPages) {
        return;
      }
      setPage(page + 1);
    },
  };
}
